/**
 * useWorkflows Hook
 * Manages workflow data fetching with search and pagination
 */

import { useState, useCallback, useEffect } from 'react';
import { workflowService, WorkflowDashboard } from '@/services/features/WorkflowService';
import { useAuth } from '@/contexts/AuthContext';

export interface UseWorkflowsResult {
  workflows: WorkflowDashboard[];
  loading: boolean;
  error: string | null;
  currentPage: number;
  totalPages: number;
  totalCount: number;
  entriesPerPage: number;
  filterType: string;
  searchValue: string;
  setCurrentPage: (page: number) => void;
  setEntriesPerPage: (entries: number) => void;
  setFilterType: (type: string) => void;
  setSearchValue: (value: string) => void;
  handleSearch: () => void;
  clearSearch: () => void;
  refetch: () => void;
}

export const useWorkflows = (): UseWorkflowsResult => {
  const { hasuraToken } = useAuth();
  const [workflows, setWorkflows] = useState<WorkflowDashboard[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [entriesPerPage, setEntriesPerPage] = useState(50);
  const [totalCount, setTotalCount] = useState(0);
  const [filterType, setFilterType] = useState('workflow_name');
  const [searchValue, setSearchValue] = useState('');
  const [activeSearch, setActiveSearch] = useState<{
    filterType: string;
    searchValue: string;
  } | null>(null);

  const totalPages = Math.ceil(totalCount / entriesPerPage);

  const fetchWorkflows = useCallback(async () => {
    if (!hasuraToken) {
      setError('Authentication required');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const offset = (currentPage - 1) * entriesPerPage;
      
      let result;
      if (activeSearch && activeSearch.searchValue.trim()) {
        result = await workflowService.searchWorkflows(
          activeSearch.filterType,
          activeSearch.searchValue,
          offset,
          entriesPerPage,
          hasuraToken
        );
      } else {
        result = await workflowService.getWorkflows(
          offset,
          entriesPerPage,
          hasuraToken
        );
      }

      setWorkflows(result.workflows);
      setTotalCount(result.totalCount);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch workflows');
      console.error('Error fetching workflows:', err);
    } finally {
      setLoading(false);
    }
  }, [hasuraToken, currentPage, entriesPerPage, activeSearch]);

  useEffect(() => {
    fetchWorkflows();
  }, [fetchWorkflows]);

  const handleSearch = useCallback(() => {
    setActiveSearch({
      filterType,
      searchValue
    });
    setCurrentPage(1);
  }, [filterType, searchValue]);

  const clearSearch = useCallback(() => {
    setSearchValue('');
    setActiveSearch(null);
    setCurrentPage(1);
  }, []);

  const handleSetCurrentPage = useCallback((page: number) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
    }
  }, [totalPages]);

  const handleSetEntriesPerPage = useCallback((entries: number) => {
    setEntriesPerPage(entries);
    setCurrentPage(1);
  }, []);

  return {
    workflows,
    loading,
    error,
    currentPage,
    totalPages,
    totalCount,
    entriesPerPage,
    filterType,
    searchValue,
    setCurrentPage: handleSetCurrentPage,
    setEntriesPerPage: handleSetEntriesPerPage,
    setFilterType,
    setSearchValue,
    handleSearch,
    clearSearch,
    refetch: fetchWorkflows
  };
};
